import React, { createContext, useState, useEffect } from 'react';
import apiClient, { setAccessToken } from '../api/apiClient';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user on mount (check for valid session via cookie)
  const loadUser = async (retries = 3, delay = 1000) => {
    try {
      // We don't have an access token yet, but if we have a refresh cookie,
      // the interceptor will see 401 on this request, call refresh, 
      // set the access token, and then retry this request transparently.
      const { data } = await apiClient.get('/users/me');
      setUser(data);
    } catch (error) {
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        console.error("Failed to load user:", error.message);
      } else {
        console.log("No active user session (unauthenticated).");
      }

      // Retry logic for connection refused (backend starting up)
      if (error.code === 'ECONNREFUSED' && retries > 0) {
        console.log(`Backend not ready, retrying in ${delay / 1000}s... (${retries} retries left)`);
        setTimeout(() => loadUser(retries - 1, delay), delay);
        return;
      }

      // If 401 or other error, clear user
      setUser(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (identifier, password, isOtp = false, otp = null) => {
    let res;
    if (isOtp) {
      res = await apiClient.post('/users/verify-otp', {
        identifier,
        otp,
        context: 'login'
      });
    } else {
      res = await apiClient.post('/users/login', {
        email: identifier.includes('@') ? identifier : undefined,
        phone_number: !identifier.includes('@') ? identifier : undefined,
        password
      });
    }

    if (res.data.accessToken) {
      setAccessToken(res.data.accessToken);
      // After setting token, fetch user details
      const userRes = await apiClient.get('/users/me');
      setUser(userRes.data);
      return userRes.data;
    } else if (res.data.token && isOtp) { // Handle case where verify-otp might return 'token' property
      setAccessToken(res.data.token);
      const userRes = await apiClient.get('/users/me');
      setUser(userRes.data);
      return userRes.data;
    }
  };

  const socialLogin = async (payload) => {
    const res = await apiClient.post('/users/social-login', payload);
    if (res.data.accessToken) {
      setAccessToken(res.data.accessToken);
      const userRes = await apiClient.get('/users/me');
      setUser(userRes.data);
      return userRes.data;
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/users/logout');
    } catch (err) {
      console.error("Logout failed", err);
    }
    setAccessToken(null);
    setUser(null);
    // Optionally force reload to clear any other state
    // window.location.reload(); 
  };

  const authContextValue = { user, loading, login, socialLogin, logout };

  return (
    <AuthContext.Provider value={authContextValue}>
      {loading ? (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-zinc-950 text-white relative overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.08)_0,transparent_65%)] pointer-events-none" />
          
          <div className="flex flex-col items-center gap-6 z-10">
            {/* Logo */}
            <img 
              src="/assets/images/logo-dark.png" 
              alt="Say Whaat Logo" 
              className="h-28 w-auto object-contain animate-pulse" 
            />
            
            {/* Spinning Loader */}
            <div className="flex items-center gap-3 bg-zinc-900/80 border border-zinc-800/80 backdrop-blur-md px-6 py-3 rounded-full shadow-2xl">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
              </span>
              <span className="text-zinc-300 text-sm font-medium tracking-wide">
                Connecting to secure server...
              </span>
            </div>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};